// E8SquarePyramidVolumeOpenTask.js
// Zadanie otwarte E8 (2025 CZE zad. 21) - ostroslup prawidlowy czworokatny.
// Krawedz podstawy a; wysokosc H = obwod podstawy - k = 4a - k. Objetosc V = 1/3 * a^2 * H.

import React from "react";
import { BlockMath, InlineMath } from "../../../../components/math/KatexWrappers";
import EquationInput from "../../../../components/inputs/EquationInput";
import FractionInput from "../../../../components/inputs/FractionInput";
import { scoreStaged } from "../../../openTaskScoring";

const CASES = [
  { a: 3.5, k: 8 }, // H=6, P=12,25, V=24,5 (original)
  { a: 4.5, k: 8 }, // H=10, P=20,25, V=67,5
  { a: 5, k: 8 }, //   H=12, P=25, V=100
  { a: 6, k: 8 }, //   H=16, P=36, V=192
];

function pick(array) {
  return array[Math.floor(Math.random() * array.length)];
}

function formatNumber(value) {
  return Number.isInteger(value) ? String(value) : String(value).replace(".", ",");
}

function buildData(config) {
  const perimeter = 4 * config.a;
  const height = perimeter - config.k;
  const baseArea = config.a * config.a;
  const volume = (baseArea * height) / 3;
  return { ...config, perimeter, height, baseArea, volume, maxPoints: 3 };
}

function createOriginalTask() {
  return {
    type: "E8SquarePyramidVolumeOpenTask",
    data: buildData(CASES[0]),
    source: { variant: "original", exam: "E8", session: "2025_CZE", taskNumber: 21 },
    tags: ["e8", "stereometria", "ostroslup", "objetosc", "zadanie-otwarte"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  return {
    type: "E8SquarePyramidVolumeOpenTask",
    data: buildData(pick(CASES.slice(1))),
    source: { variant: "random", basedOn: "E8_2025_CZE_21" },
    tags: ["e8", "stereometria", "ostroslup", "objetosc", "zadanie-otwarte"],
  };
}

function rawValue(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value.raw === "string") return value.raw;
  return String(value);
}

function normalize(raw) {
  return rawValue(raw).toLowerCase().replace(/,/g, ".").replace(/\s+/g, "");
}

function numberTokens(raw) {
  return (normalize(raw).match(/\d+(?:\.\d+)?/g) || []).map(Number);
}

function hasNumber(raw, target, tolerance = 0.01) {
  return numberTokens(raw).some((value) => Math.abs(value - target) <= tolerance);
}

function parseNumberLike(value) {
  const raw = normalize(value);
  if (!raw) return null;
  const number = Number(raw);
  return Number.isFinite(number) ? number : null;
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Dany jest ostrosłup prawidłowy czworokątny, którego krawędź podstawy ma długość{" "}
        <InlineMath math={`${formatNumber(data.a)}\\text{ cm}`} />. Wysokość ostrosłupa jest o{" "}
        <InlineMath math={`${data.k}\\text{ cm}`} /> mniejsza od obwodu jego podstawy.
      </p>
      <p><strong>Oblicz objętość tego ostrosłupa. Zapisz obliczenia.</strong></p>
    </div>
  );
}

export function renderInput({ data }, userAnswer = {}, onChange, disabled) {
  const setField = (field) => (value) => onChange({ ...(userAnswer || {}), [field]: value });
  return (
    <div style={{ display: "grid", gap: 14, textAlign: "left" }}>
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Krok 1. Oblicz pole podstawy.</div>
        <EquationInput value={userAnswer.baseArea || ""} onChange={setField("baseArea")} disabled={disabled} placeholder="np. (3,5)^2=12,25" showPreview basis="380px" />
      </div>
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Krok 2. Oblicz wysokość ostrosłupa.</div>
        <EquationInput value={userAnswer.height || ""} onChange={setField("height")} disabled={disabled} placeholder="np. 4*3,5-8=6" showPreview basis="380px" />
      </div>
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Krok 3. Wpisz objętość ostrosłupa.</div>
        <FractionInput suffixLatex="\\text{cm}^3" value={userAnswer.volume} onChange={setField("volume")} disabled={disabled} placeholder="np. 24,5" />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer = {}) {
  const baseOk = hasNumber(userAnswer.baseArea, data.baseArea);
  const heightOk = hasNumber(userAnswer.height, data.height);
  const result = parseNumberLike(userAnswer.volume);
  const volumeOk = result != null && Math.abs(result - data.volume) <= 0.01;
  return scoreStaged({
    resultOk: volumeOk,
    milestones: [baseOk, heightOk],
    maxScore: data.maxPoints,
    parts: { "pole podstawy": baseOk, "wysokość": heightOk, "objętość": volumeOk },
  });
}

export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>{"Przyk\u0142adowa poprawna odpowied\u017a:"}</div>
      <BlockMath math={`P=(${formatNumber(data.a)})^2=${formatNumber(data.baseArea)}\\text{ cm}^2`} />
      <BlockMath math={`H=${formatNumber(data.perimeter)}-${data.k}=${formatNumber(data.height)}\\text{ cm}`} />
      <BlockMath math={`V=\\frac{1}{3}\\cdot ${formatNumber(data.baseArea)}\\cdot ${formatNumber(data.height)}=${formatNumber(data.volume)}\\text{ cm}^3`} />
      <div>{"Odpowied\u017a: obj\u0119to\u015b\u0107 ostros\u0142upa jest r\u00f3wna "}<InlineMath math={`${formatNumber(data.volume)}\\text{ cm}^3`} />{"."}</div>
    </div>
  );
}

export function renderUserAnswer(userAnswer = {}) {
  const result = parseNumberLike(userAnswer.volume);
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>Pole podstawy: {userAnswer.baseArea || <i>(brak)</i>}</div>
      <div>Wysokość: {userAnswer.height || <i>(brak)</i>}</div>
      <div>Objętość: {result == null ? <i>(brak)</i> : <InlineMath math={`${formatNumber(result)}\\text{ cm}^3`} />}</div>
    </div>
  );
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p><strong>Krok 1.</strong> Podstawą jest kwadrat o boku <InlineMath math={`${formatNumber(data.a)}\\text{ cm}`} />:</p>
      <BlockMath math={`P=(${formatNumber(data.a)})^2=${formatNumber(data.baseArea)}\\text{ cm}^2`} />
      <p>
        <strong>Krok 2.</strong> Obwód podstawy to <InlineMath math={`4\\cdot ${formatNumber(data.a)}=${formatNumber(data.perimeter)}\\text{ cm}`} />,
        więc wysokość:
      </p>
      <BlockMath math={`H=${formatNumber(data.perimeter)}-${data.k}=${formatNumber(data.height)}\\text{ cm}`} />
      <p><strong>Krok 3.</strong> Obliczamy objętość ostrosłupa:</p>
      <BlockMath math={`V=\\frac{1}{3}\\cdot ${formatNumber(data.baseArea)}\\cdot ${formatNumber(data.height)}=${formatNumber(data.volume)}\\text{ cm}^3`} />
    </div>
  );
}
